'use client';
import React, { useState } from 'react';

interface ServiceDetail {
  id: string;
  icon: string;
  title: string;
  description: string;
  features: string[];
}

const serviceDetails: ServiceDetail[] = [
  {
    id: 'folding',
    icon: 'fa-layer-group',
    title: 'Folding Services',
    description:
      'Our state-of-the-art folding equipment handles everything from simple letter folds to complex multi-panel configurations. We work with a wide range of paper stocks, weights, and sizes to deliver precise, consistent results on every job.',
    features: [
      'Letter fold (tri-fold)',
      'Z-fold / Accordion fold',
      'Half fold / Bi-fold',
      'Gate fold',
      'Roll fold',
      'French fold',
      'Custom fold configurations',
    ],
  },
  {
    id: 'collating',
    icon: 'fa-copy',
    title: 'Collating Services',
    description:
      'Accurate collating is critical for multi-page documents, reports, manuals, and marketing materials. Our automated collating systems ensure every set is complete, in the correct order, and ready for distribution or binding.',
    features: [
      'Multi-page document collating',
      'Booklet and catalog assembly',
      'Insert collating',
      'Sequential numbering',
      'Tabbed section assembly',
      'Quality control verification',
      'High-volume production runs',
    ],
  },
  {
    id: 'cutting',
    icon: 'fa-cut',
    title: 'Cutting & Trimming',
    description:
      'Precision cutting and trimming services to exact specifications. Our guillotine cutters and programmable trimmers deliver clean, accurate cuts on all paper types and thicknesses, from single sheets to large stacks.',
    features: [
      'Guillotine cutting',
      'Die-cutting',
      'Perforating',
      'Scoring and creasing',
      'Slit cutting',
      'Exact-size trimming',
      'Custom shapes and sizes',
    ],
  },
  {
    id: 'bindery',
    icon: 'fa-book',
    title: 'Bindery Services',
    description:
      'Complete bindery solutions for all types of publications and documents. Whether you need saddle-stitched booklets, perfect-bound manuals, or spiral-bound presentations, we have the equipment and expertise to deliver professional results.',
    features: [
      'Saddle stitching',
      'Perfect binding',
      'Spiral / coil binding',
      'Wire-O binding',
      'Comb binding',
      'Tape binding',
      'Padding and gluing',
    ],
  },
  {
    id: 'mailing',
    icon: 'fa-envelope-open-text',
    title: 'Mailing & Fulfillment',
    description:
      'End-to-end mailing and fulfillment services to get your printed materials into the right hands. From addressing and inserting to sealing and metering, we handle the complete mailing process efficiently and cost-effectively.',
    features: [
      'Inkjet addressing',
      'Inserting and stuffing',
      'Envelope sealing',
      'Postage metering',
      'USPS presort services',
      'List management',
      'Bulk mailing',
    ],
  },
  {
    id: 'finishing',
    icon: 'fa-star',
    title: 'Specialty Finishing',
    description:
      'Add a professional touch to your printed materials with our specialty finishing services. From lamination and UV coating to embossing and foil stamping, we offer a wide range of finishing options to make your print projects stand out.',
    features: [
      'Lamination (gloss/matte)',
      'UV coating',
      'Aqueous coating',
      'Embossing / debossing',
      'Foil stamping',
      'Shrink wrapping',
      'Packaging and kitting',
    ],
  },
];

export default function DetailedServicesSection() {
  const [activeTab, setActiveTab] = useState('folding');
  const active = serviceDetails.find((s) => s.id === activeTab) || serviceDetails[0];

  return (
    <div
      id="services-detail"
      style={{
        width: '100%',
        backgroundColor: '#fff',
        borderTop: '4px solid #0e6585',
      }}
    >
      <div style={{ maxWidth: '1140px', margin: '0 auto', padding: '60px 30px' }}>
        <h2
          style={{
            fontFamily: 'Lato, sans-serif',
            fontSize: '36px',
            fontWeight: 900,
            color: '#0e6585',
            textTransform: 'uppercase',
            textAlign: 'center',
            marginBottom: '8px',
            marginTop: 0,
          }}
        >
          Our Services
        </h2>
        <p
          style={{
            textAlign: 'center',
            fontFamily: 'Lato, sans-serif',
            fontSize: '16px',
            color: '#666',
            marginBottom: '36px',
          }}
        >
          Comprehensive Printers&apos; Services for Every Business Need
        </p>

        {/* Tab Buttons */}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            borderBottom: '2px solid #e0e0e0',
            marginBottom: '0',
            gap: '0',
          }}
        >
          {serviceDetails.map((s) => (
            <button
              key={s.id}
              onClick={() => setActiveTab(s.id)}
              style={{
                padding: '12px 16px',
                fontSize: '12px',
                fontFamily: 'Lato, sans-serif',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                cursor: 'pointer',
                border: 'none',
                borderBottom: activeTab === s.id ? '3px solid #0e6585' : '3px solid transparent',
                backgroundColor: activeTab === s.id ? '#f7f7f7' : 'transparent',
                color: activeTab === s.id ? '#0e6585' : '#777',
                transition: 'all 0.2s',
                outline: 'none',
                marginBottom: '-2px',
              }}
            >
              <i className={`fas ${s.icon}`} style={{ marginRight: '6px' }}></i>
              {s.title.split(' ')[0]}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div
          style={{
            backgroundColor: '#f7f7f7',
            padding: '30px',
            borderRadius: '0 0 3px 3px',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '30px', alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 300px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div
                  style={{
                    width: '50px',
                    height: '50px',
                    backgroundColor: '#0e6585',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <i className={`fas ${active.icon}`} style={{ color: '#fff', fontSize: '20px' }}></i>
                </div>
                <h3
                  style={{
                    fontFamily: '"Pragati Narrow", sans-serif',
                    fontSize: '24px',
                    fontWeight: 700,
                    color: '#333',
                    textTransform: 'uppercase',
                    margin: 0,
                  }}
                >
                  {active.title}
                </h3>
              </div>
              <p
                style={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '14px',
                  lineHeight: '1.7',
                  color: '#555',
                  marginBottom: '20px',
                }}
              >
                {active.description}
              </p>
              <a
                href="#contact"
                style={{
                  display: 'inline-block',
                  backgroundColor: '#0e6585',
                  color: '#fff',
                  padding: '10px 20px',
                  fontSize: '12px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  textDecoration: 'none',
                  borderRadius: '12px',
                  fontFamily: 'Lato, sans-serif',
                  transition: 'background 0.2s',
                }}
                className="service-cta-btn"
              >
                Request This Service
              </a>
            </div>
            <div style={{ flex: '1 1 250px' }}>
              <h4
                style={{
                  fontFamily: 'Lato, sans-serif',
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#0e6585',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                  marginBottom: '12px',
                  marginTop: 0,
                }}
              >
                What We Offer:
              </h4>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {active.features.map((feature) => (
                  <li
                    key={feature}
                    style={{
                      fontFamily: 'Lato, sans-serif',
                      fontSize: '13px',
                      color: '#555',
                      padding: '6px 0',
                      borderBottom: '1px solid #e0e0e0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <i className="fas fa-check" style={{ color: '#0e6585', fontSize: '11px', flexShrink: 0 }}></i>
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        .service-cta-btn:hover { background-color: #333 !important; }
      `}</style>
    </div>
  );
}
